'use client'

import { useEffect, useRef } from 'react'

import { runWhenIdle } from '@/lib/run-when-idle'
import { useLogger } from '@/hooks/use-logger'
import { useSession } from '@/hooks/use-session'
import { reportUserData, retractUserData } from '@/services/gtm/report-user-data'

/**
 * Reports a logged-in visitor's identity as the hashed advertising match signal,
 * and announces it so a tag has a moment to carry it. No conversion happens on this
 * app, and the page's tags run at container load — before a session resolves —
 * so the announcement is the only point at which the signal can be read truthfully.
 *
 * The identity arrives in two stages, the mirror's email and then the name, so
 * this runs twice for the same visitor and the second payload is a superset. The
 * cleanup supersedes an in-flight digest from the first: nothing orders two
 * digests, and the queue model keeps whichever lands last.
 *
 * Losing the session retracts what was reported. The tags read the accumulated
 * model on every ordinary event, so a visitor who logs out would otherwise keep
 * being reported until the document is replaced — on a shared device, as the
 * person who logged in after them.
 */
export function SessionUserData() {
    const { session } = useSession()
    const logger = useLogger({ module: 'session-user-data' })
    const { isAuthenticated, email, firstName, lastName } = session
    // The session starts logged-out on every document (the server snapshot is
    // session-blind), so a retraction is owed only once something was reported.
    const reported = useRef(false)

    useEffect(() => {
        if (!isAuthenticated || !email) {
            if (reported.current) {
                const outcome = retractUserData()
                // Still owed if it failed: forgetting it leaves the previous
                // visitor on the queue with nothing left to clear them.
                reported.current = outcome !== 'retracted'
                if (outcome === 'failed') {
                    logger.warn('session user_data not retracted')
                }
            }
            return
        }

        let superseded = false
        const cancel = runWhenIdle(() => {
            void reportUserData(
                { email, firstName, lastName },
                { isSuperseded: () => superseded },
            ).then((outcome) => {
                if (outcome === 'reported') {
                    reported.current = true
                }
                if (outcome === 'failed') {
                    logger.warn('session user_data not reported')
                }
            })
        })

        return () => {
            superseded = true
            cancel()
        }
    }, [isAuthenticated, email, firstName, lastName, logger])

    return null
}
